import React from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import type { DatasetRef } from '@semantical/modules/semantical-core';

type Props = {
    selectedDatasetId: string | null;
    availableDatasets: DatasetRef[];
    onDatasetChange: (id: string | null) => void;
    darkMode?: boolean;
};

type State = {
    isOpen: boolean;
    dropdownPosition: { top: number; left: number; width: number } | null;
};

/**
 * DatasetSelector - Per-diagram dropdown for selecting the linked dataset (KnowledgeGraph).
 *
 * Populated from the BoundedContext's datasets.
 * When a dataset is selected, new elements created in the diagram are added to that dataset.
 * When "None" is selected, element creation may be disabled or use a default.
 */
export default class DatasetSelector extends React.Component<Props, State> {
    private containerRef = React.createRef<HTMLDivElement>();
    private buttonRef = React.createRef<HTMLButtonElement>();

    state: State = {
        isOpen: false,
        dropdownPosition: null,
    };

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
    }

    handleClickOutside = (event: MouseEvent) => {
        if (this.containerRef.current && !this.containerRef.current.contains(event.target as Node)) {
            // Also check if click is inside the portal dropdown
            const dropdown = document.getElementById('dataset-selector-dropdown');
            if (dropdown && dropdown.contains(event.target as Node)) {
                return;
            }
            this.setState({ isOpen: false });
        }
    };

    handleToggle = () => {
        if (!this.state.isOpen && this.buttonRef.current) {
            const rect = this.buttonRef.current.getBoundingClientRect();
            this.setState({
                isOpen: true,
                dropdownPosition: {
                    top: rect.bottom + 4,
                    left: rect.left,
                    width: rect.width,
                },
            });
        } else {
            this.setState({ isOpen: false });
        }
    };

    handleSelect = (id: string | null) => {
        this.props.onDatasetChange(id);
        this.setState({ isOpen: false });
    };

    render() {
        const { selectedDatasetId, availableDatasets } = this.props;
        const { isOpen, dropdownPosition } = this.state;

        const selectedDataset = availableDatasets.find(d => d.id === selectedDatasetId);

        const dropdown = isOpen && dropdownPosition ? (
            <div
                id="dataset-selector-dropdown"
                className="fixed bg-surface-light dark:bg-[rgba(28,30,36,0.98)] border border-slate-200 dark:border-accent-border/20 rounded-lg shadow-[0_8px_32px_rgba(100,120,140,0.15)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden z-[10001] backdrop-blur-[12px]"
                style={{
                    top: dropdownPosition.top,
                    left: dropdownPosition.left,
                    minWidth: dropdownPosition.width,
                }}
            >
                {/* None option - no linked dataset */}
                <div
                    className={clsx(
                        "px-3 py-2.5 text-[13px] cursor-pointer transition-all duration-100 flex items-center gap-2",
                        selectedDatasetId === null
                            ? "bg-accent-teal/10 dark:bg-accent/15 text-accent-teal dark:text-accent font-semibold"
                            : "text-slate-500 dark:text-white/60 bg-transparent hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10"
                    )}
                    onClick={() => this.handleSelect(null)}
                >
                    {selectedDatasetId === null && (
                        <svg width="14" height="14" viewBox="0 0 14 14">
                            <path d="M2,7 L5,10 L12,3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    )}
                    {selectedDatasetId !== null && <div className="w-3.5" />}
                    <span className="italic">None</span>
                </div>

                {availableDatasets.length > 0 && (
                    <div className="border-t border-slate-200 dark:border-accent-border/20 my-1" />
                )}

                {availableDatasets.map(dataset => {
                    const isSelected = dataset.id === selectedDatasetId;
                    return (
                        <div
                            key={dataset.id}
                            className={clsx(
                                "px-3 py-2.5 text-[13px] cursor-pointer transition-all duration-100 flex items-center gap-2",
                                isSelected
                                    ? "bg-accent-teal/10 dark:bg-accent/15 text-accent-teal dark:text-accent font-semibold"
                                    : "text-slate-700 dark:text-white/85 bg-transparent hover:bg-accent-teal/[0.08] dark:hover:bg-accent/10"
                            )}
                            onClick={() => this.handleSelect(dataset.id)}
                        >
                            {isSelected && (
                                <svg width="14" height="14" viewBox="0 0 14 14">
                                    <path d="M2,7 L5,10 L12,3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                            {!isSelected && <div className="w-3.5" />}
                            {/* KnowledgeGraph icon */}
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-teal-500 dark:text-teal-400">
                                <circle cx="6" cy="6" r="3" stroke="currentColor" strokeWidth="2"/>
                                <circle cx="18" cy="6" r="3" stroke="currentColor" strokeWidth="2"/>
                                <circle cx="12" cy="18" r="3" stroke="currentColor" strokeWidth="2"/>
                                <path d="M8.5 7.5L10.5 16M15.5 7.5L13.5 16M9 6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            <span>{dataset.name}</span>
                        </div>
                    );
                })}
            </div>
        ) : null;

        return (
            <div ref={this.containerRef} className="relative inline-block ml-3 font-sans">
                <button
                    ref={this.buttonRef}
                    className={clsx(
                        "flex items-center gap-2 px-3 py-2 text-[13px] font-medium rounded-lg border cursor-pointer outline-none transition-all duration-150 min-w-[140px] justify-between",
                        isOpen
                            ? "bg-slate-50 dark:bg-accent/10 border-accent-teal dark:border-accent/40 text-slate-700 dark:text-white"
                            : "bg-white dark:bg-white/[0.06] border-slate-200 dark:border-accent-border text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-accent/10 hover:border-accent-teal dark:hover:border-accent/40"
                    )}
                    onClick={this.handleToggle}
                    title="Select linked dataset (KnowledgeGraph)"
                >
                    <span className={!selectedDataset ? "text-slate-400 dark:text-white/50 italic" : ""}>
                        {selectedDataset?.name || 'No Dataset'}
                    </span>
                    <svg
                        className={clsx(
                            "transition-transform duration-200 opacity-60",
                            isOpen && "rotate-180"
                        )}
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                    >
                        <path d="M3,4 L6,7 L9,4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                {dropdown && ReactDOM.createPortal(dropdown, document.body)}
            </div>
        );
    }
}
